import { useState, useEffect } from 'react'
import { Upload, Download, AlertCircle, CheckCircle, MapPin, Database } from 'lucide-react'
import { referencesAPI } from '../../services/api'
import { useReferences } from '../../hooks/useReferences'
import { Button } from '../Common/Button'
import toast from 'react-hot-toast'

export const ReferenceUpload = ({ referenceType, onSuccess }) => {
  const { downloadTemplate } = useReferences()

  const [loading, setLoading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [result, setResult] = useState(null)
  const [region, setRegion] = useState('')
  const [source, setSource] = useState('Минстрой')
  const [regions, setRegions] = useState([])
  const [loadingRegions, setLoadingRegions] = useState(false)

  const referenceInfo = {
    materials: {
      title: 'Материалы',
      description: 'Загрузите справочник материалов с региональными ценами',
      icon: '📦'
    },
    equipment: {
      title: 'Оборудование',
      description: 'Загрузите справочник оборудования с региональными расценками',
      icon: '🔧'
    },
    operations: {
      title: 'Операции',
      description: 'Загрузите справочник операций с региональными ценами',
      icon: '⚙️'
    }
  }

  const info = referenceInfo[referenceType]

  // Загружаем список регионов при монтировании
  useEffect(() => {
    loadRegions()
  }, [])

  const loadRegions = async () => {
    setLoadingRegions(true)
    try {
      const response = await referencesAPI.getRegions()
      setRegions(response.data || [])
    } catch (error) {
      console.error('Ошибка загрузки регионов:', error)
    } finally {
      setLoadingRegions(false)
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      handleFile(files[0])
    }
  }

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = async (file) => {
    // Проверяем регион
    if (!region || region.trim() === '') {
      toast.error('Пожалуйста, укажите регион')
      return
    }

    // Поддерживаем как CSV так и Excel
    const isCSV = file.name.endsWith('.csv')
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls')

    if (!isCSV && !isExcel) {
      toast.error('Требуется CSV или Excel файл (.xlsx, .xls)')
      return
    }

    setLoading(true)
    setResult(null)
    setUploadProgress(0)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('region', region.trim())
      formData.append('source', (source || 'Минстрой').trim() || 'Минстрой')

      let response

      if (isExcel) {
        // Загрузка Excel
        response = await referencesAPI.uploadExcel(referenceType, formData)
      } else {
        // Загрузка CSV (старый метод, если нужен)
        response = await referencesAPI.upload(referenceType, formData)
      }

      const data = response.data

      setResult({
        status: data.status,
        message: data.message,
        itemsCount: data.records_count || data.items_count || 0,
        warnings: data.warnings || [],
        errors: data.errors || []
      })

      if (data.status === 'success') {
        toast.success(`Загружено ${data.records_count || data.items_count} записей для региона "${region}"`)

        // Обновляем список регионов
        await loadRegions()

        if (onSuccess) {
          onSuccess()
        }
      } else if (data.status === 'validation_error') {
        toast.error(`Ошибки валидации: ${data.total_errors || data.errors?.length || 0}`)
      } else {
        toast.error(data.message || 'Ошибка загрузки')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setResult({
        status: 'error',
        message: errorMessage,
        itemsCount: 0,
        errors: [errorMessage]
      })
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadTemplate = async () => {
    setLoading(true)
    try {
      await downloadTemplate(referenceType)
      toast.success('Шаблон успешно скачан')
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Ошибка при скачивании шаблона'
      toast.error(errorMessage)
      console.error('Error downloading template:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">{info.icon}</span>
        <div>
          <h3 className="text-xl font-semibold">{info.title}</h3>
          <p className="text-sm text-gray-600">{info.description}</p>
        </div>
      </div>

      {/* Выбор региона */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="inline w-4 h-4 mr-1" />
          Регион ценообразования *
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="Например: Москва, Санкт-Петербург, Казань..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            list="regions-list"
            disabled={loading}
          />
          {regions.length > 0 && (
            <datalist id="regions-list">
              {regions.map((r, idx) => (
                <option key={idx} value={r.name} />
              ))}
            </datalist>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {loadingRegions 
            ? 'Загрузка регионов...' 
            : regions.length > 0 
              ? `Доступно регионов: ${regions.length}` 
              : 'Введите название региона для добавления новых цен'}
        </p>
      </div>

      {/* Источник справочника (площадка) */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Database className="inline w-4 h-4 mr-1" />
          Источник справочника
        </label>
        <input
          type="text"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="Минстрой"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          list="sources-list"
          disabled={loading}
        />
        <datalist id="sources-list">
          <option value="Минстрой" />
        </datalist>
        <p className="text-xs text-gray-500 mt-1">
          Площадка, с которой загружаются цены. По умолчанию — «Минстрой».
        </p>
      </div>

      {/* Зона загрузки */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50'
        }`}
      >
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileInput}
          disabled={loading || !region}
          className="hidden"
          id={`file-input-${referenceType}`}
        />

        <label
          htmlFor={`file-input-${referenceType}`}
          className={`cursor-pointer ${(!region || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-lg font-medium mb-2">
            Перетащите Excel или CSV файл сюда
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Поддерживаются форматы: .xlsx, .xls, .csv
          </p>
          <Button
            variant="primary"
            size="sm"
            disabled={loading || !region}
          >
            {!region ? 'Укажите регион сначала' : 'Выбрать файл'}
          </Button>
        </label>
      </div>

      {/* Кнопка скачивания шаблона */}
      <div className="mt-4 flex justify-center">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2"
          disabled={loading}
        >
          <Download size={16} />
          Скачать CSV шаблон
        </Button>
      </div>

      {/* Результат */}
      {result && (
        <div
          className={`mt-4 p-4 rounded-lg ${
            result.status === 'success'
              ? 'bg-green-50 border border-green-200'
              : result.status === 'validation_error'
              ? 'bg-yellow-50 border border-yellow-200'
              : 'bg-red-50 border border-red-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {result.status === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                result.status === 'validation_error' ? 'text-yellow-600' : 'text-red-600'
              }`} />
            )}
            <div className="flex-1">
              <p
                className={`font-medium ${
                  result.status === 'success'
                    ? 'text-green-800'
                    : result.status === 'validation_error'
                    ? 'text-yellow-800'
                    : 'text-red-800'
                }`}
              >
                {result.message}
              </p>
              {result.itemsCount > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Загружено записей: {result.itemsCount} (регион: {region})
                </p>
              )}

              {/* Предупреждения */}
              {result.warnings && result.warnings.length > 0 && (
                <div className="mt-2 text-sm">
                  <p className="font-medium text-yellow-700">Предупреждения:</p>
                  <ul className="list-disc list-inside text-yellow-600 mt-1">
                    {result.warnings.slice(0, 5).map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                  {result.warnings.length > 5 && (
                    <p className="text-xs text-yellow-500 mt-1">
                      ...и ещё {result.warnings.length - 5} предупреждений
                    </p>
                  )}
                </div>
              )}

              {/* Ошибки */}
              {result.errors && result.errors.length > 0 && result.status !== 'success' && (
                <div className="mt-2 text-sm">
                  <p className="font-medium text-red-700">Ошибки:</p>
                  <ul className="list-disc list-inside text-red-600 mt-1">
                    {result.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                  {result.errors.length > 5 && (
                    <p className="text-xs text-red-500 mt-1">
                      ...и ещё {result.errors.length - 5} ошибок
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Индикатор загрузки */}
      {loading && (
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 animate-pulse"
              style={{ width: `${uploadProgress || 50}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-2 text-center">
            Обработка файла...
          </p>
        </div>
      )}

      {/* Информация о текущих регионах */}
      {regions.length > 0 && !loading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-800 mb-2">
            📍 Доступные регионы:
          </p>
          <div className="flex flex-wrap gap-2">
            {regions.slice(0, 10).map((r, idx) => (
              <button
                key={idx}
                onClick={() => setRegion(r.name)}
                className="px-2 py-1 bg-white border border-blue-300 rounded text-xs text-blue-700 hover:bg-blue-100 transition-colors"
              >
                {r.name}
                {referenceType === 'materials' && r.materials_count > 0 && (
                  <span className="ml-1 text-blue-500">({r.materials_count})</span>
                )}
                {referenceType === 'equipment' && r.equipment_count > 0 && (
                  <span className="ml-1 text-blue-500">({r.equipment_count})</span>
                )}
                {referenceType === 'operations' && r.operations_count > 0 && (
                  <span className="ml-1 text-blue-500">({r.operations_count})</span>
                )}
              </button>
            ))}
            {regions.length > 10 && (
              <span className="px-2 py-1 text-xs text-gray-500">
                +{regions.length - 10} ещё
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
